import React, { useState, useEffect, useRef } from "react";
import "../index.css";
import Counter from "./Counter";
import {
  EColorStatus,
  EPaymentStatus,
  EShippingType,
  EStatusValue,
  IProduct,
  type Orders,
} from "../../../common/interfaces";
import { KitchenService } from "../../../services/KitchenService";
import { CommonService } from "../../../services/CommonService";
import moment from "moment";

import { OverlayTrigger, Popover, Spinner } from "react-bootstrap";
// @ts-ignore
import { Tooltip } from "bootstrap";

// Función auxiliar para formatear cantidades
const formatQuantity = (quantity: string | number): string => {
  const num = Number(quantity);
  return Number.isInteger(num) ? num.toString() : num.toFixed(2);
};

// Función para formatear cantidades con unidades, convirtiendo kg a g cuando es menor a 1
const formatQuantityWithUnit = (quantity: string | number, unit: string = ''): { value: string, unit: string } => {
  const num = Number(quantity);

  // Si la unidad es Kg y el valor es menor a 1, convertir a gramos
  if (unit === 'Kg' && num < 1) {
    const grams = Math.round(num * 1000);
    return { value: grams.toString(), unit: 'g' };
  }

  // Para otros casos, usar el formateo normal
  return { value: formatQuantity(num), unit };
};


const Card: React.FC<
  Orders & { className?: string; updateCallback?: () => void; filterType?: 'kitchen' | 'salesfloor' }
> = (props) => {
  const { id, className, updateCallback, status, kitchen_status, floor_status, filterType = 'kitchen' } = props;

  // Determinar qué estado usar según el contexto
  const currentStatus = filterType === 'salesfloor'
    ? (floor_status || status)
    : (kitchen_status || status);
  const [disable, setDisable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasScroll, setHasScroll] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);

  // Función para inicializar los tooltips
  const initTooltips = () => {
    // Primero, eliminar cualquier tooltip existente
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    [...tooltipTriggerList].forEach(tooltipTriggerEl => {
      const tooltip = Tooltip.getInstance(tooltipTriggerEl);
      if (tooltip) {
        tooltip.dispose();
      }
    });

    // Luego, inicializar los tooltips nuevamente
    [...tooltipTriggerList].forEach(tooltipTriggerEl => {
      new Tooltip(tooltipTriggerEl, {
        trigger: 'hover focus',
        html: true,
        container: 'body',
        boundary: 'viewport'
      });
    });
  };

  // Inicializar los tooltips cuando el componente se monta
  useEffect(() => {
    initTooltips();

    // Limpiar los tooltips al desmontar el componente
    return () => {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      [...tooltipTriggerList].forEach(tooltipTriggerEl => {
        const tooltip = Tooltip.getInstance(tooltipTriggerEl);
        if (tooltip) {
          tooltip.dispose();
        }
      });
    };
  }, []);

  // Reinicializar los tooltips cuando cambia el estado del pedido
  useEffect(() => {
    initTooltips();
  }, [status, props.payment_status]);

  // Check if the list has scrollable content
  const checkScrollable = () => {
    if (listRef.current) {
      const { scrollHeight, clientHeight, scrollTop } = listRef.current;
      const hasScrollContent = scrollHeight > clientHeight;
      const isAtBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;

      setHasScroll(hasScrollContent);
      setIsScrolledToBottom(isAtBottom);
    }
  };

  // Add scroll event listener and check initial scroll state
  useEffect(() => {
    const listElement = listRef.current;
    if (listElement) {
      // Check initial state
      checkScrollable();

      // Add scroll event listener
      listElement.addEventListener('scroll', checkScrollable);

      // Add resize observer to check when content changes
      const resizeObserver = new ResizeObserver(() => {
        checkScrollable();
      });

      resizeObserver.observe(listElement);

      // Cleanup
      return () => {
        listElement.removeEventListener('scroll', checkScrollable);
        resizeObserver.disconnect();
      };
    }
  }, [props.detalles]);

  const startOrder = (orderId: string) => {
    setDisable(true);
    setLoading(true);
    KitchenService.trackOrderStatus(orderId, {
      status: filterType === 'salesfloor' ? "Preparing" : "Cooking",
      status_type: filterType === 'salesfloor' ? 'floor' : 'kitchen',
    }).then(() => {
      setDisable(false);
      setLoading(false);
      updateCallback && updateCallback();
    }).catch((error) => {
      console.error("Error starting order:", error);
      CommonService.toast("Error al iniciar la orden. Intente nuevamente.", "error");
      setDisable(false);
      setLoading(false);
    });
  };

  const setDoneOrder = (orderId: string) => {
    setDisable(true);
    setLoading(true);
    KitchenService.trackOrderStatus(orderId, {
      status: "Done",
      status_type: filterType === 'salesfloor' ? 'floor' : 'kitchen',
    }).then(() => {
      setDisable(false);
      setLoading(false);
      updateCallback && updateCallback();
      // Notificar a otras pestañas sobre el cambio
      localStorage.setItem('order-status-changed', JSON.stringify({
        orderId: orderId,
        action: 'completed',
        timestamp: Date.now()
      }));
    }).catch((error) => {
      console.error("Error completing order:", error);
      CommonService.toast("Error al terminar la orden. Intente nuevamente.", "error");
      setDisable(false);
      setLoading(false);
    });
  };

  const setShipedOrder = (orderId: string, saleType?: string) => {
    setDisable(true);
    setLoading(true);
    KitchenService.trackOrderStatus(orderId, {
      status: saleType === "shipped" ? "InTransit" : "Shipped",
      status_type: filterType === 'salesfloor' ? 'floor' : 'kitchen',
    }).then((response) => {
      // Verificar si la respuesta indica éxito o fallo
      if (response && response.success === false) {
        // Mostrar el mensaje específico del backend
        CommonService.toast(response.message || "Error al entregar la orden. Intente nuevamente.", "error");
      } else {
        // Éxito - actualizar la interfaz local
        updateCallback && updateCallback();
        // Notificar a otras pestañas sobre el cambio
        localStorage.setItem('order-status-changed', JSON.stringify({
          orderId: orderId,
          action: 'shipped',
          timestamp: Date.now()
        }));
      }
      setDisable(false);
      setLoading(false);
    }).catch((error) => {
      console.error("Error shipping order:", error);
      // Verificar si el error contiene un mensaje específico del backend
      const errorMessage = error?.response?.data?.message ||
                          error?.message ||
                          "Error al entregar la orden. Intente nuevamente.";
      CommonService.toast(errorMessage, "error");
      setDisable(false);
      setLoading(false);
    });
  };

  return (
    <>
      <div className={className}>
        <div
          className={`card ${
            EColorStatus[currentStatus as keyof typeof EColorStatus]
          }`}
          // style={{ height: "550px", overflow: "auto" }}
        >
          <div className="card-body d-flex flex-column" style={{ height: "450px", overflow: "hidden", position: "relative" }}>
            {/* Schedule Section - Only show if scheduled - At the very top */}
            {props.schedule !== "" && props.schedule !== null && (
              <div className="row mb-2">
                <div className="col-12 text-center">
                  <div className="alert alert-info py-1 px-2 mb-0" style={{ fontSize: "13px" }}>
                    <i className="bi bi-clock me-1"></i>
                    <b>AGENDADO:</b> {moment(props.schedule).format("hh:mm A")}
                  </div>
                </div>
              </div>
            )}

            {/* Header Section */}
            <div className="row mb-1">
              <div className="col-6" style={{"fontSize": "18px", fontWeight: "bold"}}>
                #{id}
              </div>
              <div className="col-6 text-end">
                <Counter
                  dateToFormat={props.timeFromCooking}
                  status={currentStatus}
                  info={props.history}
                />
              </div>
            </div>

            {/* Status and Via Section */}
            <div className="row mb-1">
              <div className="col-6">
                <span className="badge" style={{
                  backgroundColor: EColorStatus[currentStatus as keyof typeof EColorStatus],
                  color: "#fff",
                  fontSize: "14px",
                  padding: "4px 8px"
                }}>
                  {EStatusValue[currentStatus as keyof typeof EStatusValue]}
                </span>
              </div>
              <div className="col-6 text-end">
                <span className="badge bg-secondary">
                  {props?.origin_by == 'WEB-POS' ? `WEB-${props?.alias_id}` : props?.origin_by}
                </span>
              </div>
            </div>

            {/* Customer and Shipping Info */}
            <div className="row mb-2">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center" style={{ fontSize: "14px" }}>
                  <div>
                    <i className="bi bi-person me-1"></i>
                    {props?.cliente == null ? "Público en General" : props?.cliente?.nombre}
                  </div>
                  <div className="d-flex gap-2">
                    {props.shipped_type && (
                      <span className="badge bg-light text-dark" style={{ fontSize: "14px" }}>
                        {EShippingType[props.shipped_type as keyof typeof EShippingType] ?? ""}
                      </span>
                    )}
                    {props.payment_status && (
                      <span className={`badge ${props.payment_status === "pending" ? "bg-warning text-dark" : "bg-success text-white"}`} style={{ fontSize: "14px" }}>
                        {EPaymentStatus[props.payment_status as keyof typeof EPaymentStatus] ?? ""}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Main content area - will flex grow to fill available space */}
            <div className="flex-grow-1 overflow-hidden d-flex flex-column">
              {/* Products List Section - will take all available space */}
              <div className="row flex-grow-1 mb-2 overflow-hidden">
                <div className="col-12 h-100">
                  <div className="card border-0 shadow-sm h-100 d-flex flex-column position-relative">
                    <ul
                      ref={listRef}
                      className="list-group list-group-flush flex-grow-1 overflow-auto"
                    >
                      {(() => {
                        // Filtrar productos que son servicios (ej: "ASADO POR KILO")
                        // Estos productos tienen force_kitchen=1 y no deben mostrarse
                        const filteredDetalles = props.detalles.filter(detalle => {
                          // Validar que el producto tenga nombre antes de procesarlo
                          if (!detalle.producto?.name) {
                            return true; // Mantener productos sin nombre (pueden ser errores del sistema)
                          }

                          // Ocultar productos cuyo nombre contenga "ASADO POR KILO" o "ASADO POR KG"
                          const productName = detalle.producto.name.toUpperCase();
                          const isGrillingService = productName.includes("ASADO POR KILO") ||
                                                   productName.includes("ASADO POR KG");
                          return !isGrillingService;
                        });

                        return filteredDetalles.map((detalle, i) => (
                            <OverlayTrigger
                              key={detalle.id}
                              placement="auto"
                              overlay={
                                <Popover style={{background: "#ffff"}}>
                                  <Popover.Header as="h2"><b>RECETA</b></Popover.Header>
                                  <Popover.Body style={{color:"black"}}>
                                    {detalle.producto.assembled_products ? detalle.producto.assembled_products.map((product: IProduct) => (
                                      <div key={product.id} className="d-flex justify-content-between align-items-center" style={{ fontSize: "16px", paddingBottom: "8px"}}>
                                        <div className="text-start" style={{ width: "calc(100% - 70px)" }}>
                                          - <span style={{ wordWrap: "break-word", hyphens: "auto" }}>{product.name}</span>
                                        </div>
                                        <div style={{ width: "70px", textAlign: "right" }}>
                                          {(() => {
                                            const formatted = formatQuantityWithUnit(product.pivot.quantity, product.unit || '');
                                            return <b>{formatted.value} {formatted.unit}</b>;
                                          })()}
                                        </div>
                                      </div>
                                    )) : ''}
                                  </Popover.Body>
                                </Popover>
                              }
                            >
                              <li
                                className="list-group-item py-2 px-3"
                              >
                                <div className="d-flex align-items-start mb-1">
                                  <span className="badge me-2" style={{ fontSize: "14px", minWidth: "40px", textAlign: "center" }}>
                                    {i + 1}/{filteredDetalles.length}
                                  </span>
                                  <div className="me-auto" style={{ fontSize: "15px", flex: "1", minWidth: "0" }}>
                                    <div className="fw-bold" style={{ wordWrap: "break-word", hyphens: "auto" }}>
                                      {detalle?.producto?.name
                                        ? (detalle.producto.name.includes("PARRILLADA")
                                            ? detalle.producto.name.split(" CON")[0]
                                            : detalle.producto.name)
                                        : "Producto sin nombre"}
                                    </div>
                                    {/* Mostrar badge de asado en una línea separada para mejor responsividad */}
                                    {(() => {
                                      // Verificar si tiene servicio de asado en las opciones
                                      const hasGrillingInOptions = detalle.options &&
                                        (detalle.options.toUpperCase().includes("ASADO") ||
                                         detalle.options.toUpperCase().includes("GRILLING"));

                                      // Verificar si hay otro producto en la orden que sea "ASADO POR KILO" para este producto
                                      const hasGrillingService = detalle.producto?.name && props.detalles.some(d => {
                                        // Validar que el producto tenga nombre
                                        if (!d.producto?.name || !detalle.producto?.name) {
                                          return false;
                                        }

                                        const productName = d.producto.name.toUpperCase();
                                        const currentProductName = detalle.producto.name.toUpperCase();

                                        return (productName.includes("ASADO POR KILO") || productName.includes("ASADO POR KG")) &&
                                          d.options &&
                                          d.options.toUpperCase().includes(currentProductName.substring(0, 10));
                                      });

                                      return (hasGrillingInOptions || hasGrillingService) && (
                                        <div className="mt-1">
                                          <span
                                            className="badge bg-danger"
                                            style={{
                                              fontSize: "11px",
                                              padding: "3px 8px",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: "4px"
                                            }}
                                            title="Este producto lleva servicio de asado"
                                          >
                                            🔥 ASADO
                                          </span>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  <div className="ms-2" style={{ width: "80px", flexShrink: 0, textAlign: "center" }}>
                                    <span className="badge bg-primary rounded-pill" style={{
                                      fontSize: "13px",
                                      width: "100%",
                                      display: "inline-block"
                                    }}>
                                      {(() => {
                                        const formatted = formatQuantityWithUnit(detalle.cantidad, detalle.producto.unit || '');
                                        return `${formatted.value} ${formatted.unit}`;
                                      })()}
                                    </span>
                                  </div>
                                </div>
                                {detalle.options !== "" && (
                                  <div style={{ fontSize: "13px", paddingLeft: "40px" }}>
                                    {detalle.options?.split(",").map((option, i) => (
                                      option.trim() !== "" && (
                                        <div key={i} className="text-truncate" style={{ maxWidth: "100%" }}>
                                          - {option}
                                        </div>
                                      )
                                    ))}
                                  </div>
                                )}
                              </li>
                            </OverlayTrigger>
                          ));
                      })()}
                    </ul>
                    {/* Scroll indicator */}
                    {hasScroll && !isScrolledToBottom && (
                      <div
                        className="position-absolute bottom-0 start-0 end-0 text-center"
                        style={{
                          background: "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 40%, rgba(255,255,255,1) 100%)",
                          height: "40px",
                          pointerEvents: "none",
                          zIndex: 2,
                          paddingTop: "15px"
                        }}
                      >
                        <div className="d-flex justify-content-center align-items-center">
                          <i className="bi bi-chevron-double-down bounce-animation" style={{
                            fontSize: "24px",
                            color: "#dc3545",
                            fontWeight: "bold"
                          }}></i>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              {props.notes_order && (
                <div className="row mb-2">
                  <div className="col-12">
                    <div className="alert alert-warning py-1 px-2 mb-0" style={{ fontSize: "12px" }}>
                      <div className="fw-bold mb-1">NOTAS:</div>
                      <div style={{ maxHeight: "50px", overflowY: "auto" }}>
                        {props.notes_order}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Date Footer - Fixed at bottom */}
            <div className="row mt-auto">
              <div className="col-12 text-center">
                <small className="bg-white text-dark d-inline-flex align-items-center px-2 py-1 rounded">
                  <i className="bi bi-calendar-event me-1"></i>
                  {moment(props.created_at).format("DD/MM/YYYY hh:mm A")}
                </small>
              </div>
            </div>

          </div>

          <div className="btn-group" role="group">
            {currentStatus === "Schedule" && (
              <>
                <button
                  disabled={disable}
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    startOrder(`${id}`);
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      <span>Procesando...</span>
                    </>
                  ) : (
                    "Iniciar"
                  )}
                </button>
              </>
            )}
            {currentStatus === "Pending" && (
              <>
                <button
                  disabled={disable}
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    startOrder(`${id}`);
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      <span>Procesando...</span>
                    </>
                  ) : (
                    "Iniciar"
                  )}
                </button>
              </>
            )}
            {(currentStatus === "Cooking" || currentStatus === "Preparing") && (
              <>
                <button
                  disabled={disable}
                  type="button"
                  className="btn btn-warning"
                  onClick={() => {
                    setDoneOrder(`${id}`);
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      <span>Procesando...</span>
                    </>
                  ) : (
                    "Terminar"
                  )}
                </button>
              </>
            )}
            {currentStatus === "Done" && (
              <>
                {props.payment_status === "pending" &&
                props.shipped_type !== "shipped" &&
                props.shipped_type !== "inRoom" ? (
                  <div
                    className="btn btn-warning w-100"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    data-bs-html="true"
                    data-bs-custom-class="custom-tooltip"
                    title="<div style='text-align: left;'><strong>⚠️ Atención:</strong><br>Este pedido requiere pago antes de ser entregado.<br><br>Por favor, indique al cliente que debe realizar el pago en caja para poder entregar su pedido.</div>"
                    style={{ cursor: "not-allowed", fontSize: "15px", fontWeight: "bold" }}
                  >
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    Pendiente de Pago
                  </div>
                ) : props.shipped_type === "shipped" ? (
                  // No mostrar botón para envíos a domicilio
                  <div
                    className="btn btn-info w-100"
                    style={{ cursor: "default", fontSize: "15px", fontWeight: "bold" }}
                  >
                    <i className="bi bi-truck me-2"></i>
                    Se entrega desde Caja
                  </div>
                ) : (
                  <button
                    disabled={disable}
                    type="button"
                    className="btn btn-success"
                    onClick={() => {
                      setShipedOrder(`${id}`, props.shipped_type);
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        <span>Procesando...</span>
                      </>
                    ) : (
                      "Entregar"
                    )}
                  </button>
                )}
              </>
            )}
            {currentStatus === "Shipped" && (
              <>
                {(() => {
                  // Determinar el estado de la otra área
                  const otherAreaStatus = filterType === 'kitchen'
                    ? (props.floor_status || props.status)
                    : (props.kitchen_status || props.status);

                  // Si la otra área también está "Shipped", mostrar estado final
                  if (otherAreaStatus === "Shipped") {
                    return (
                      <div
                        className="btn btn-secondary w-100"
                        style={{ cursor: "default", fontSize: "15px", fontWeight: "bold" }}
                      >
                        <i className="bi bi-check-circle-fill me-2"></i>
                        Pedido Completamente Entregado
                      </div>
                    );
                  }

                  // Si la otra área no está "Shipped", mostrar estado de espera
                  const areaName = filterType === 'kitchen' ? 'Piso de Venta' : 'Cocina';
                  return (
                    <div
                      className="btn btn-warning w-100"
                      style={{ cursor: "default", fontSize: "15px", fontWeight: "bold" }}
                    >
                      <i className="bi bi-clock-fill me-2"></i>
                      Esperando entrega en {areaName}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Card;
